import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useLocation, useNavigate } from 'react-router-dom';

import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSModal from '../../../components/designSystem/DSModal';
import RichTextEditor, { type RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import { uploadProjectImage } from '../../../Module/Project/Presentation/projectActions';
import { convertToVwMob } from '../../../Utils/responsive';
import { PATHS } from '../../../routes/paths';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import searchIcon from '../../../assets/Icon/project/setting/search_icon.png';
import searchActiveIcon from '../../../assets/Icon/project/setting/search_active_icon.png';
import penIcon from '../../../assets/Icon/project/setting/pen_icon.png';
import penActiveIcon from '../../../assets/Icon/project/setting/pen_active_icon.png';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { theme } from '../../../styles/theme';
import { getProject, updateProject } from '../../../Module/Project/Presentation/projectActions';
import { selectGetProjectStatus, selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { useTranslation } from 'react-i18next';
import { extractDigits, formatCurrencyInput, formatKoreanCurrency, parseCurrencyNumber } from '../../../Utils/formatCurrency';
import { hasLegacyStoryMarkup, prepareStoryHtmlForEditor, restoreStoryHtmlFromEditor, sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import { getCloudFrontImageUrl, THUMBNAIL_DIMENSIONS } from '../../../Utils/cloudFrontImage';
import { isImageSizeAllowed } from '../../../Utils/imageUpload';
import projectActiveIcon from '../../../assets/Icon/sidbar/project_active_icon.png';
import postIcon from '../../../assets/Icon/sidbar/post_icon.png';
import sponsorIcon from '../../../assets/Icon/sidbar/sponsor_icon.png';
import payIcon from '../../../assets/Icon/sidbar/pay_icon.png';
import pictureIconImg from '../../../assets/Icon/project/picture_icon.png';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');

type TabKey = 'basic' | 'story';

const STORY_IMAGE_LIMIT = 3;
/** 스크롤 상단 패딩·헤더 실제 높이와 동일 (padding + 1줄 medium20 + border) */
const STORY_EDIT_HEADER_HEIGHT = 64;
const STORY_KEYBOARD_SCROLL_PAD = 250;
const STORY_EDITOR_BOTTOM_SPACER_PX = 200;
const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

const isValidHttpUrl = (raw: string) => {
  if (!raw.trim()) return false;
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/** 헤더 저장 중 Lottie — DotLottie 기본 contain으로 캔버스 대비 작게 그려지는 현상 보완 */
/** 한 줄 텍스트(20px·line-height 150%)와 동일 높이 — 저장 시 헤더가 늘어나지 않음 */
const StoryEditSaveSpinner = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  flex-shrink: 0;
  overflow: hidden;
  line-height: 0;

  canvas {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
`;

/** 애셋 여백 대응 (DSLoading LottieInner와 동일) */
const StoryEditLottieInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(1.35);
  transform-origin: center center;
`;

const ProjectSettingsMob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('translation');
  const processingLabel = stripEllipsis(t('common.actions.processing'));

  const getStatus = useAppSelector(selectGetProjectStatus);
  const project = useAppSelector(selectSelectedProject);

  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [currency, setCurrency] = useState<'KRW' | 'USD'>('KRW');
  const [goalAmountKrw, setGoalAmountKrw] = useState<string>('');
  const [goalAmountUsd, setGoalAmountUsd] = useState<string>('');
  const goalAmount = currency === 'KRW' ? goalAmountKrw : goalAmountUsd;
  const setGoalAmount = currency === 'KRW' ? setGoalAmountKrw : setGoalAmountUsd;
  const [title, setTitle] = useState<string>('');
  const [projectDesc, setProjectDesc] = useState<string>('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');

  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isLegacyStoryEditorMode, setIsLegacyStoryEditorMode] = useState(false);
  const [exitConfirmTarget, setExitConfirmTarget] = useState<'media' | null>(null);
  const [tempMediaType, setTempMediaType] = useState<'image' | 'video' | 'none'>('image');
  const [tempMediaFile, setTempMediaFile] = useState<File | null>(null);
  const [tempMediaPreview, setTempMediaPreview] = useState<string | null>(null);
  const [tempVideoUrl, setTempVideoUrl] = useState<string>('');
  const [tempVideoUrlFocused, setTempVideoUrlFocused] = useState(false);
  const [tempVideoPreviewEnabled, setTempVideoPreviewEnabled] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const storyEditorRef = useRef<RichTextEditorHandle>(null);
  const legacyStoryEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const storyOverlayScrollRef = useRef<HTMLDivElement>(null);
  const storyKeyboardFooterRef = useRef<HTMLDivElement>(null);
  const prevStoryEmbeddedImageCountRef = useRef(0);
  const [isStoryEditing, setIsStoryEditing] = useState(false);
  /** updateProject 직후가 아니라 본문 재조회·알림까지 포함한 저장 플로우 전체 */
  const [saveInProgress, setSaveInProgress] = useState(false);
  const titleTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustTitleTextareaHeight = useCallback(() => {
    const el = titleTextareaRef.current;
    if (!el) return;

    const computed = window.getComputedStyle(el);
    const lineHeight = Number.parseFloat(computed.lineHeight || '0') || 0;
    const maxHeight = lineHeight > 0 ? lineHeight * 2 : 0;

    el.style.height = 'auto';
    if (maxHeight > 0) {
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
      return;
    }

    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = 'hidden';
  }, []);

  const openFilePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      picker.call(input);
      return;
    }
    input.click();
  };

  const isLoading = getStatus === 'loading' && (!project || project.project_id !== projectId);
  const isSaving = saveInProgress;

  const headerTitle = useMemo(() => {
    const trimmedTitle = sanitizeProjectTitle(project?.title ?? title ?? '').trim();
    return trimmedTitle || t('page.projectSettings.placeholders.projectTitlePreviewMobile');
  }, [project?.title, title, t]);

  useEffect(() => {
    if (!projectId) {
      navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
      return;
    }
    // 이미 로딩 중이거나 현재 프로젝트가 요청하려는 프로젝트와 같으면 중단
    if (getStatus === 'loading' || project?.project_id === projectId) return;
    dispatch(getProject({ project_id: projectId }));
  }, [dispatch, projectId, getStatus, project?.project_id, navigate]);

  useEffect(() => {
    if (!project) return;
    setCurrency((project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW');
    const projectCurrency = (project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    const formatted = project.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(prev => (projectCurrency === 'KRW' ? formatted : prev));
    setGoalAmountUsd(prev => (projectCurrency === 'USD' ? formatted : prev));
    setTitle(sanitizeProjectTitle(project.title ?? ''));
    setProjectDesc(String(project.project_desc ?? ''));
    setMediaPreview(project.image_url ? String(project.image_url) : null);
    setVideoUrl(project.video_url ? String(project.video_url) : '');
    setIsLegacyStoryEditorMode(false);
  }, [project]);

  useEffect(() => {
    adjustTitleTextareaHeight();
  }, [title, activeTab, adjustTitleTextareaHeight]);

  useEffect(() => {
    if (activeTab !== 'story') setIsStoryEditing(false);
  }, [activeTab]);

  useEffect(() => {
    if (!isStoryEditing) return;
    const scrollY = window.scrollY;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      htmlOverflow: html.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.position = prev.bodyPosition;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.body.style.right = prev.bodyRight;
      document.body.style.width = prev.bodyWidth;
      document.body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isStoryEditing]);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const readUrlAsBase64 = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return readFileAsBase64(new File([blob], 'image.png', { type: blob.type || 'image/png' }));
  };

  const formatGoal = (raw: string, targetCurrency: 'KRW' | 'USD') => {
    return formatCurrencyInput(raw, targetCurrency);
  };

  const handleGoalAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart !== selectionEnd) return;
    if (currency === 'KRW' && value.startsWith('₩') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'KRW') : '');
    } else if (currency === 'USD' && value.startsWith('$') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'USD') : '');
    }
  };

  const handleCancel = () => {
    const projectCurrency = (project?.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    setCurrency(projectCurrency);
    const formatted = project?.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(projectCurrency === 'KRW' ? formatted : '');
    setGoalAmountUsd(projectCurrency === 'USD' ? formatted : '');
    setTitle(sanitizeProjectTitle(project?.title ?? ''));
    setProjectDesc(String(project?.project_desc ?? ''));
    setMediaPreview(project?.image_url ? String(project.image_url) : null);
    setVideoUrl(project?.video_url ? String(project.video_url) : '');
    setMediaFile(null);
    setIsLegacyStoryEditorMode(false);
  };

  const handleStoryEditCancel = () => {
    setProjectDesc(String(project?.project_desc ?? ''));
    setIsLegacyStoryEditorMode(false);
    setIsStoryEditing(false);
  };

  const handleStartStoryEditing = () => {
    const rawProjectDesc = String(project?.project_desc ?? '');
    const useLegacyEditorMode = hasLegacyStoryMarkup(rawProjectDesc);
    setIsLegacyStoryEditorMode(useLegacyEditorMode);
    setProjectDesc(useLegacyEditorMode ? prepareStoryHtmlForEditor(rawProjectDesc) : rawProjectDesc);
    setIsStoryEditing(true);
  };

  const handleSave = async (overrideData?: { mediaFile?: File | null; projectDesc?: string; videoUrl?: string; mediaPreview?: string | null }) => {
    if (!projectId) return;

    const titleToValidate = sanitizeProjectTitle(title).trim();
    const minGoalVal = currency === 'KRW' ? 35000 : 35;
    if (titleToValidate.length < 4 || titleToValidate.length > 40 || numericGoal < minGoalVal) {
      // eslint-disable-next-line no-alert
      alert(
        titleToValidate.length < 4 || titleToValidate.length > 40
          ? t('page.projectSettings.helpers.titleLength')
          : currency === 'KRW'
            ? t('page.projectSettings.helpers.minGoal')
            : t('page.projectSettings.helpers.minGoalUSD'),
      );
      return;
    }

    const activeEl = document.activeElement as HTMLElement | null;
    if (activeEl?.isContentEditable) {
      activeEl.blur();
      await new Promise((r) => requestAnimationFrame(r));
    }

    setSaveInProgress(true);
    const partner = String(project?.partner ?? (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF');
    const numeric = parseCurrencyNumber(currency === 'KRW' ? goalAmountKrw : goalAmountUsd);

    try {
      let imageBase64: string | undefined;

      // overrideData에 mediaFile이 명시적으로 null이면 삭제를 의미
      const targetMediaFile = overrideData && 'mediaFile' in overrideData ? overrideData.mediaFile : mediaFile;
      const targetMediaPreview = overrideData && 'mediaPreview' in overrideData ? overrideData.mediaPreview : mediaPreview;

      if (targetMediaFile) {
        imageBase64 = await readFileAsBase64(targetMediaFile);
      } else {
        imageBase64 = '';
      }

      let updatedProjectDesc = overrideData?.projectDesc ?? projectDesc;
      if (isLegacyStoryEditorMode) {
        updatedProjectDesc = restoreStoryHtmlFromEditor(updatedProjectDesc);
      }
      const overrideVideoUrl = overrideData?.videoUrl;

      // Handle blob URLs in projectDesc
      const blobUrls = updatedProjectDesc.match(/src="blob:[^"]+"/g) || [];
      for (const match of blobUrls) {
        const url = match.slice(5, -1);
        try {
          const resBlob = await fetch(url);
          const blob = await resBlob.blob();
          const dataUrl = await readFileAsBase64(new File([blob], 'image.png', { type: blob.type }));
          const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

          const uploadRes = await dispatch(
            uploadProjectImage({
              partner,
              project_id: projectId,
              projectpost_id: '',
              language_code: 'ko',
              isuse: true,
              image: base64,
            }),
          ).unwrap();

          if (uploadRes.image_url) {
            updatedProjectDesc = updatedProjectDesc.replace(url, uploadRes.image_url);
          }
        } catch (uploadErr) {
          console.error('Embedded image upload failed:', uploadErr);
        }
      }

      updatedProjectDesc = sanitizeProjectDescHtml(updatedProjectDesc);

      const updatePayload: any = {
        project_id: projectId,
        partner,
        title: sanitizeProjectTitle(title).trim().slice(0, 40),
        goal_amount: numeric ? numeric : undefined,
        currency_type: currency,
        project_desc: updatedProjectDesc,
        is_image_display: String(overrideVideoUrl ?? project?.video_url ?? '').trim().length === 0,
        video_url: overrideVideoUrl ?? project?.video_url,
        user_id: project?.user_id,
        user_full_name: project?.user_full_name,
        type: project?.type,
        start_date: project?.start_date,
        end_date: project?.end_date,
        status: project?.status,
      };

      updatePayload.image = imageBase64;
      if (!imageBase64 && targetMediaPreview) {
        updatePayload.image_url = targetMediaPreview;
      }

      await dispatch(updateProject(updatePayload)).unwrap();
      try {
        await dispatch(getProject({ project_id: projectId })).unwrap();
      } catch (refetchErr) {
        console.error('Failed to refetch project after save', refetchErr);
      }
      setSaveInProgress(false);
      // eslint-disable-next-line no-alert
      alert(t('common.messages.saved'));
      setIsStoryEditing(false);
    } catch (e: any) {
      setSaveInProgress(false);
      // eslint-disable-next-line no-alert
      alert(e?.message || (typeof e === 'string' ? e : t('page.projectSettings.errors.saveFailed')));
    }
  };

  const handleOpenMediaModal = () => {
    const nextMediaType = project?.video_url ? 'video' : project?.image_url ? 'image' : 'none';
    setTempMediaType(nextMediaType);
    setTempMediaPreview(mediaPreview);
    setTempMediaFile(null);
    setTempVideoUrl(project?.video_url ? String(project.video_url) : '');
    setTempVideoPreviewEnabled(Boolean(project?.video_url));
    setIsMediaModalOpen(true);
  };

  const handleSaveMediaModal = async () => {
    if (tempMediaType === 'none') {
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl('');
      await handleSave({ mediaFile: null, videoUrl: '', mediaPreview: null });
    } else if (tempMediaType === 'video') {
      const trimmed = tempVideoUrl.trim();
      const nextVideoUrl = isValidHttpUrl(trimmed) ? trimmed : '';
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl(nextVideoUrl);
      await handleSave({ mediaFile: null, videoUrl: nextVideoUrl, mediaPreview: null });
    } else {
      if (tempMediaFile) {
        setMediaFile(tempMediaFile);
        setMediaPreview(tempMediaPreview);
      }
      setVideoUrl('');
      await handleSave({ mediaFile: tempMediaFile, videoUrl: '' });
    }
    setIsMediaModalOpen(false);
  };

  const fundingLabel = project?.type === 'OT'
    ? t('page.projectManagement.fundingType.oneTime')
    : t('page.projectManagement.fundingType.recurring');
  const createdText = project?.created_at ? String(project.created_at).slice(0, 10).replaceAll('-', '.') : 'YYYY.MM.DD';
  const keymanText = project?.keyman ? t('common.labels.present') : t('common.labels.none');
  const supportPercent =
    typeof (project?.support_project as any)?.support_percent === 'string'
      ? (project?.support_project as any).support_percent
      : '3';
  const supportText = t('page.projectSettings.labels.supporting', { percent: supportPercent });

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isImageSizeAllowed(file)) {
        alert(t('common.errors.imageTooLarge'));
        e.target.value = '';
        return;
      }
      setTempMediaFile(file);
      setTempMediaPreview(URL.createObjectURL(file));
    }
  };

  const buildVideoEmbedUrl = (rawUrl: string) => {
    if (!rawUrl.trim()) return '';
    try {
      const url = new URL(rawUrl.trim());
      const host = url.hostname.replace('www.', '');
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  };

  const videoEmbedUrl = useMemo(() => buildVideoEmbedUrl(videoUrl), [videoUrl]);
  const videoPreviewSrc = useMemo(() => {
    const trimmed = videoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return videoEmbedUrl || trimmed;
  }, [videoEmbedUrl, videoUrl]);

  const tempVideoEmbedUrl = useMemo(() => buildVideoEmbedUrl(tempVideoUrl), [tempVideoUrl]);
  const tempVideoPreviewSrc = useMemo(() => {
    const trimmed = tempVideoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return tempVideoEmbedUrl || trimmed;
  }, [tempVideoEmbedUrl, tempVideoUrl]);
  const showTempVideoUrlError = Boolean(tempVideoUrl.trim()) && !isValidHttpUrl(tempVideoUrl.trim());
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const showGoalPreview = currentLang === 'ko' && currency === 'KRW' && numericGoal >= 10000;
  const goalPreviewText = showGoalPreview ? formatKoreanCurrency(numericGoal) : '';

  const titleTrimmed = useMemo(() => sanitizeProjectTitle(title).trim(), [title]);
  const minGoal = currency === 'KRW' ? 35000 : 35;
  const goalAmountError = goalAmount.trim() !== '' && numericGoal < minGoal;
  const titleError = titleTrimmed.length > 0 && (titleTrimmed.length < 4 || titleTrimmed.length > 40);
  const canSaveBasic = titleTrimmed.length >= 4 && titleTrimmed.length <= 40 && numericGoal >= minGoal;

  const storyEmbeddedImageCount = useMemo(
    () => (projectDesc.match(/<figure/g) || []).length,
    [projectDesc],
  );
  // Legacy HTML도 그대로 렌더/편집해서 <br>는 줄바꿈으로 보이고, 추가 사진도 넣을 수 있게 처리한다.
  const isLegacyStoryHtml = false;
  const legacyStoryText = useMemo(
    () => projectDesc.replace(/<br\s*\/?>/gi, '\n'),
    [projectDesc],
  );
  const readonlyStoryHtml = useMemo(
    () => (hasLegacyStoryMarkup(projectDesc) ? prepareStoryHtmlForEditor(projectDesc) : projectDesc),
    [projectDesc],
  );
  const isStoryImageLimitReached = storyEmbeddedImageCount >= STORY_IMAGE_LIMIT;

  const scrollStoryEditorIntoView = useCallback(() => {
    const scrollEl = storyOverlayScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const r = range.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  const scrollStoryTailIntoView = useCallback(() => {
    const scrollEl = storyOverlayScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;
    const images = editorEl.querySelectorAll<HTMLElement>('figure[data-embed="story-image"]');
    const lastImage = images.length ? images[images.length - 1] : null;
    const anchorRect = lastImage?.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const visibleBottom = scrollRect.bottom - 156;
    const targetBottom = Math.max(anchorRect ? anchorRect.bottom + 36 : 0, editorRect.bottom);
    if (targetBottom > visibleBottom) {
      scrollEl.scrollTop += targetBottom - visibleBottom;
    }
  }, []);

  const liftStoryEditorScroll = useCallback((amount: number) => {
    if (!isStoryEditing) return;
    window.requestAnimationFrame(() => {
      storyOverlayScrollRef.current?.scrollBy({ top: amount, behavior: 'auto' });
    });
  }, [isStoryEditing]);

  const focusStoryEditor = useCallback(() => {
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      storyEditorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        scrollStoryEditorIntoView();
        scrollStoryTailIntoView();
        liftStoryEditorScroll(220);
        storyEditorRef.current?.focus();
      });
      rafIds.push(rid);
    };

    [0, 90, 200].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [liftStoryEditorScroll, scrollStoryEditorIntoView, scrollStoryTailIntoView]);

  const focusLegacyStoryEditor = useCallback(() => {
    const editor = legacyStoryEditorRef.current;
    if (!editor) return;
    editor.focus();
    const end = editor.value.length;
    try {
      editor.setSelectionRange(end, end);
    } catch {
      // ignore selection failures on unsupported inputs
    }
  }, []);

  useEffect(() => {
    if (!isStoryEditing || !window.visualViewport) return;

    const onViewportChange = () => {
      if (!storyKeyboardFooterRef.current || !window.visualViewport) return;
      const visualViewport = window.visualViewport;
      const layoutHeight = document.documentElement.clientHeight;
      const visualHeight = visualViewport.height;
      const visualTop = visualViewport.offsetTop;
      const offset = Math.max(0, layoutHeight - visualHeight - visualTop);
      storyKeyboardFooterRef.current.style.bottom = `${offset}px`;
    };

    onViewportChange();
    window.visualViewport.addEventListener('resize', onViewportChange);
    window.visualViewport.addEventListener('scroll', onViewportChange);
    return () => {
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.visualViewport?.removeEventListener('scroll', onViewportChange);
    };
  }, [isStoryEditing]);

  useEffect(() => {
    if (!isStoryEditing) return;
    if (isLegacyStoryHtml) return undefined;
    return focusStoryEditor();
  }, [isStoryEditing, isLegacyStoryHtml, focusStoryEditor]);

  useLayoutEffect(() => {
    if (!isStoryEditing) return;
    if (isLegacyStoryHtml) {
      focusLegacyStoryEditor();
      return;
    }
    storyEditorRef.current?.focusAtEnd();
  }, [focusLegacyStoryEditor, isStoryEditing, isLegacyStoryHtml]);

  useEffect(() => {
    if (!isStoryEditing) {
      prevStoryEmbeddedImageCountRef.current = storyEmbeddedImageCount;
      return;
    }
    if (storyEmbeddedImageCount > prevStoryEmbeddedImageCountRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          storyEditorRef.current?.focus();
          scrollStoryEditorIntoView();
          scrollStoryTailIntoView();
        });
      });
    }
    prevStoryEmbeddedImageCountRef.current = storyEmbeddedImageCount;
  }, [isStoryEditing, storyEmbeddedImageCount, scrollStoryEditorIntoView, scrollStoryTailIntoView]);

  const handleRequestCloseModal = () => {
    setExitConfirmTarget('media');
  };

  const handleConfirmExit = () => {
    if (exitConfirmTarget === 'media') setIsMediaModalOpen(false);
    setExitConfirmTarget(null);
  };

  const handleCurrencyChange = (next: 'KRW' | 'USD') => {
    if (next === currency) return;
    setCurrency(next);
    setGoalAmountKrw('');
    setGoalAmountUsd('');
  };

  if (isLoading) {
    return (
      <MobilePageLayout title={t('page.projectSettings.pageTitle')} showBottomNav={false}>
        <DSLoading />
      </MobilePageLayout>
    );
  }

  return (
    <MobilePageLayout
      title={t('page.projectSettings.pageTitle')}
      hideHeader={isStoryEditing}
      hideFooter={isStoryEditing}
      showBottomNav={!isStoryEditing}
    >
      <Page>
        <ProjectDetailHeader title={headerTitle} projectId={projectId} variant="mobile" />

        <PageTitle>{t('page.projectSettings.pageTitle')}</PageTitle>

        <TabsBox>
          <TabButton type="button" $active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
            <TabIcon src={activeTab === 'basic' ? searchActiveIcon : searchIcon} alt="" />
            {t('page.projectSettings.tabs.basic')}
          </TabButton>
          <TabButton type="button" $active={activeTab === 'story'} onClick={() => setActiveTab('story')}>
            <TabIcon src={activeTab === 'story' ? penActiveIcon : penIcon} alt="" />
            {t('page.projectSettings.tabs.story')}
          </TabButton>
        </TabsBox>

        {activeTab === 'basic' ? (
          <>
            <PanelCard style={{ background: '#F9FAFB', border : 'none', paddingTop : '8px', paddingLeft : '6px' , paddingRight: '6px'}}>
              <FieldTitle>{t('page.projectSettings.sections.goalAmount')}</FieldTitle>

              <CurrencyRow>
                <CurrencyBtn type="button" $active={currency === 'KRW'} onClick={() => handleCurrencyChange('KRW')}>
                  <RadioDot $active={currency === 'KRW'} /> {t('common.currency.krw')}
                </CurrencyBtn>
                <CurrencyBtn type="button" $active={currency === 'USD'} onClick={() => handleCurrencyChange('USD')}>
                  <RadioDot $active={currency === 'USD'} /> {t('common.currency.usd')}
                </CurrencyBtn>
              </CurrencyRow>

              <InputBox $error={goalAmountError}>
                <InputLabel>{t('page.projectSettings.labels.goalAmount')}</InputLabel>
                <InputField
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(formatGoal(e.target.value, currency))}
                  onKeyDown={handleGoalAmountKeyDown}
                  placeholder={t('page.projectSettings.placeholders.goalAmount')}
                  inputMode="numeric"
                  $error={goalAmountError}
                />
              </InputBox>

              {showGoalPreview ? <GoalPreview>{goalPreviewText}</GoalPreview> : null}

              <HelperText $error={goalAmountError}>
                {currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}
              </HelperText>
              <FieldTitle style={{marginTop : '18px'}}>{t('page.projectSettings.sections.projectTitle')}</FieldTitle>
              <TitleInputBox $error={titleError}>
                <InputLabel>{t('page.projectSettings.labels.projectTitle')}</InputLabel>
                <InputTextArea
                  ref={titleTextareaRef}
                  value={title}
                  onChange={(e) => {
                    setTitle(sanitizeProjectTitle(e.target.value).slice(0, 40));
                    setTimeout(adjustTitleTextareaHeight, 0);
                  }}
                  onFocus={adjustTitleTextareaHeight}
                  placeholder={t('page.projectSettings.placeholders.projectTitleHint')}
                  rows={2}
                  $error={titleError}
                />
              </TitleInputBox>
              <HelperText $error={titleError}>{t('page.projectSettings.helpers.titleLengthShort')}</HelperText>

            <ButtonRow>
              <ActionButton type="button" $variant="cancel" onClick={handleCancel} disabled={isSaving}>
                {t('common.actions.cancel')}
              </ActionButton>
              <ActionButton
                type="button"
                $variant="save"
                $loading={isSaving}
                onClick={() => handleSave()}
                disabled={isSaving || !projectId || !canSaveBasic}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
              >
                {isSaving ? (
                  <LoadingIcon
                    src={loadingLottie}
                    autoplay
                    loop
                    aria-hidden
                    style={{ width: 12, height: 12 }}
                  />
                ) : t('common.actions.save')}
              </ActionButton>
            </ButtonRow>
            </PanelCard>

            <PanelCard>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.fundingType')}</InfoLabel>
                <InfoValue>{fundingLabel}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.createdAt')}</InfoLabel>
                <InfoValue>{createdText}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.keyman')}</InfoLabel>
                <InfoValue>{keymanText}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.missionFund')}</InfoLabel>
                <InfoValue>{supportText}</InfoValue>
              </InfoRow>
            </PanelCard>
          </>
        ) : (
          <>
            <PanelCard>
              <CardHeaderRow>
                <CardTitle>{t('page.projectSettings.sections.media')}</CardTitle>
                <EditButton type="button" onClick={handleOpenMediaModal}>
                  {t('page.projectSettings.actions.edit')}
                </EditButton>
              </CardHeaderRow>
              <MediaBox $hasImage={!videoPreviewSrc && Boolean(mediaPreview)}>
                {videoPreviewSrc ? (
                  <MediaIframe
                    src={videoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : mediaPreview ? (
                  <MediaImg src={getCloudFrontImageUrl(mediaPreview, THUMBNAIL_DIMENSIONS) || mediaPreview} alt="" />
                ) : (
                  <EmptyText>{t('page.postManagement.modal.mediaType.none')}</EmptyText>
                )}
                {(videoPreviewSrc || mediaPreview) ? <Badge>{t('common.labels.main')}</Badge> : null}
              </MediaBox>
            </PanelCard>

            <PanelCard>
              <CardHeaderRow>
                <CardTitle>{t('page.projectSettings.sections.story')}</CardTitle>
                {!isStoryEditing ? (
                  <EditButton type="button" onClick={handleStartStoryEditing}>
                    {t('page.projectSettings.actions.edit')}
                  </EditButton>
                ) : null}
              </CardHeaderRow>
              {!isStoryEditing ? (
                <StoryReadonlyBox>
                  {projectDesc ? (
                    <StoryReadonlyHtml html={readonlyStoryHtml} />
                  ) : (
                    <EmptyText>{t('page.projectSettings.empty.story')}</EmptyText>
                  )}
                </StoryReadonlyBox>
              ) : null}
            </PanelCard>
          </>
        )}

        <BottomInset />
      </Page>

      {isStoryEditing ? (
        <StoryEditOverlay role="dialog" aria-modal="true" aria-label={t('page.projectSettings.sections.story')}>
          <StoryEditShell>
            <StoryEditHeader>
              <StoryEditHeaderBtn type="button" onClick={handleStoryEditCancel}>
                {t('common.actions.close')}
              </StoryEditHeaderBtn>
              <StoryEditHeaderBtn
                type="button"
                $primary
                disabled={isSaving || !projectId || !canSaveBasic}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
                onClick={() => {
                  (document.activeElement as HTMLElement | null)?.blur?.();
                  void handleSave();
                }}
              >
                {isSaving ? (
                  <StoryEditSaveSpinner aria-hidden>
                    <StoryEditLottieInner>
                      <DotLottieReact
                        src={loadingLottie}
                        autoplay
                        loop
                        layout={{ fit: 'cover', align: [0.5, 0.5] }}
                        renderConfig={{ autoResize: true }}
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      />
                    </StoryEditLottieInner>
                  </StoryEditSaveSpinner>
                ) : (
                  t('common.actions.save')
                )}
              </StoryEditHeaderBtn>
            </StoryEditHeader>
            <StoryEditScroll
              ref={storyOverlayScrollRef}
              $topPad={STORY_EDIT_HEADER_HEIGHT}
              $bottomPad={STORY_KEYBOARD_SCROLL_PAD}
            >
              <StoryEditBody>
                <StoryEditTapArea $focused>
                  {isLegacyStoryHtml ? (
                    <StoryRawHtmlEditor
                      ref={legacyStoryEditorRef}
                      value={projectDesc}
                      onChange={(e) => setProjectDesc(e.target.value)}
                      spellCheck={false}
                    />
                  ) : (
                    <RichTextEditor
                      ref={storyEditorRef}
                      value={projectDesc}
                      onChange={(html) => setProjectDesc(html)}
                      onEnterKey={() => liftStoryEditorScroll(96)}
                      onFocus={() => {
                        requestAnimationFrame(() => {
                          storyOverlayScrollRef.current?.scrollBy({ top: 180, behavior: 'auto' });
                          scrollStoryEditorIntoView();
                          scrollStoryTailIntoView();
                        });
                      }}
                      onImageInserted={() => {
                        requestAnimationFrame(() => {
                          storyEditorRef.current?.focus();
                          window.setTimeout(() => {
                            storyEditorRef.current?.focus();
                          }, 60);
                          requestAnimationFrame(() => {
                            scrollStoryEditorIntoView();
                            scrollStoryTailIntoView();
                            liftStoryEditorScroll(220);
                          });
                        });
                      }}
                      editorBottomPaddingPx={STORY_EDITOR_BOTTOM_SPACER_PX}
                      placeholder={t('page.projectSettings.modal.storyPlaceholder')}
                      pictureIconSrc="/Image/posts/photoIcon.png"
                      maxImages={STORY_IMAGE_LIMIT}
                      imageCount={storyEmbeddedImageCount}
                      showToolbar={false}
                    />
                  )}
                </StoryEditTapArea>
              </StoryEditBody>
            </StoryEditScroll>
            {!isLegacyStoryHtml ? (
              <StoryKeyboardFooter className="keyboard-toolbar" ref={storyKeyboardFooterRef}>
                <StoryAddPhotoButton
                  type="button"
                  onClick={() => storyEditorRef.current?.openImagePicker()}
                  disabled={isStoryImageLimitReached}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <StoryAddPhotoLabel>
                    <StoryAddPhotoIcon src="/Image/posts/black_photo.png" alt={t('page.postManagement.modal.photoAlt')} />
                    {t('page.postManagement.modal.addPhoto', { count: storyEmbeddedImageCount })}
                  </StoryAddPhotoLabel>
                </StoryAddPhotoButton>
              </StoryKeyboardFooter>
            ) : null}
          </StoryEditShell>
        </StoryEditOverlay>
      ) : null}

      {!isStoryEditing ? (
      <BottomTabBar>
        <BottomTab type="button" onClick={() => navigate(PATHS.POST_MANAGEMENT, { state: { projectId } })}>
          <BottomTabIcon src={postIcon} alt="" />
          {t('page.postManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={sponsorIcon} alt="" />
          {t('page.donorManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={payIcon} alt="" />
          {t('page.paymentStatus.pageTitle')}
        </BottomTab>
        <BottomTab type="button" $active>
          <BottomTabIcon src={projectActiveIcon} alt="" />
          {t('page.projectManagement.manage.settings')}
        </BottomTab>
      </BottomTabBar>
      ) : null}

      <DSModal
        open={Boolean(exitConfirmTarget)}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmTarget(null)}
        actions={[
          { label: t('common.exitConfirm.leave'), variant: 'secondary', onClick: handleConfirmExit },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmTarget(null) },
        ]}
      />

      {/* Media Edit Modal */}
      {isMediaModalOpen && (
        <ModalOverlay onClick={() => setIsMediaModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('page.projectSettings.modal.editTitle')}</ModalTitle>
              <CloseBtn onClick={handleRequestCloseModal}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <SectionDesc>{t('page.postManagement.modal.mediaSectionTitle')}</SectionDesc>
              <RadioRow>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'image'}
                    onChange={() => setTempMediaType('image')}
                  />
                  {t('page.postManagement.modal.mediaType.image')}
                </RadioItem>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'video'}
                    onChange={() => setTempMediaType('video')}
                  />
                  {t('page.postManagement.modal.mediaType.video')}
                </RadioItem>
              </RadioRow>

              {tempMediaType === 'image' && (
                <FileRow>
                  <FileNameBox>{tempMediaFile?.name || t('page.postManagement.modal.uploadHintImage')}</FileNameBox>
                  <AttachBtn type="button" onClick={() => openFilePicker(mediaInputRef.current)}>{t('common.actions.attach')}</AttachBtn>
                  <input
                    id="modal-media-upload-mob"
                    type="file"
                    accept="image/*"
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                    onChange={handleMediaChange}
                    ref={mediaInputRef}
                  />
                </FileRow>
              )}

              {tempMediaType === 'video' && (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showTempVideoUrlError ? 'error' : tempVideoUrlFocused ? 'focus' : tempVideoUrl.trim() ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(tempVideoUrlFocused || tempVideoUrl.trim()) && <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>}
                        <VideoUrlInput
                          placeholder={tempVideoUrlFocused || tempVideoUrl.trim() ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={tempVideoUrl}
                          onChange={(e) => {
                            setTempVideoUrl(e.target.value);
                            setTempVideoPreviewEnabled(true);
                          }}
                          onFocus={() => setTempVideoUrlFocused(true)}
                          onBlur={() => setTempVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {tempVideoUrl.trim() ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setTempVideoUrl('');
                            setTempVideoPreviewEnabled(false);
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showTempVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                </>
              )}

              <PreviewArea
                $hasMedia={
                  (tempMediaType === 'image' && Boolean(tempMediaPreview)) ||
                  Boolean(tempMediaType === 'video' && tempVideoPreviewEnabled && tempVideoPreviewSrc)
                }
              >
                {tempMediaType === 'none' ? (
                  <PreviewEmptyWrap>
                    <PreviewEmpty>{t('page.projectSettings.media.empty')}</PreviewEmpty>
                  </PreviewEmptyWrap>
                ) : tempMediaType === 'video' && tempVideoPreviewEnabled && tempVideoPreviewSrc ? (
                  <PreviewIframe
                    src={tempVideoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : tempMediaType === 'video' ? (
                  <PreviewEmptyWrap>
                    <PreviewEmpty>
                      {t('page.projectSettings.media.linkPreviewHintMobile', {
                        defaultValue: t('page.projectSettings.media.linkPreviewHint'),
                      })}
                    </PreviewEmpty>
                    <PreviewIconSlot>
                      <PreviewVideoIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="70"
                        height="70"
                        viewBox="0 0 70 70"
                        fill="none"
                        aria-hidden
                        focusable="false"
                      >
                        <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                        <path
                          d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z"
                          stroke="#BCBCBC"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </PreviewVideoIcon>
                    </PreviewIconSlot>
                  </PreviewEmptyWrap>
                ) : tempMediaPreview ? (
                  <>
                    <PreviewImg src={tempMediaPreview} alt="" />
                    <Badge>{t('page.postManagement.modal.mainBadge')}</Badge>
                  </>
                ) : (
                  <PreviewEmptyWrap>
                    <PreviewEmpty>
                      {t('page.postManagement.modal.previewHintImageMobile', {
                        defaultValue: t('page.postManagement.modal.previewHintImage'),
                      })}
                    </PreviewEmpty>
                    <PreviewIconSlot>
                      <PreviewIcon src={pictureIconImg} alt="" />
                    </PreviewIconSlot>
                  </PreviewEmptyWrap>
                )}
              </PreviewArea>
            </ModalBody>
            <ModalFooter>
              <ModalActionBtn $variant="cancel" onClick={() => setIsMediaModalOpen(false)} disabled={isSaving}>
                {t('common.actions.cancel')}
              </ModalActionBtn>
              <ModalActionBtn
                $variant="save"
                $loading={isSaving}
                onClick={handleSaveMediaModal}
                disabled={isSaving}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
              >
                {isSaving ? (
                  <LoadingIcon
                    src={loadingLottie}
                    autoplay
                    loop
                    aria-hidden
                    style={{ width: 12, height: 12 }}
                  />
                ) : t('common.actions.save')}
              </ModalActionBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </MobilePageLayout>
  );
};

export default ProjectSettingsMob;

const BottomTabBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${convertToVwMob(84)};
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 100;
`;

const BottomTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
  cursor: pointer;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const BottomTabIcon = styled.img`
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  object-fit: contain;
`;

const Page = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(14)};
`;

const PageTitle = styled.h2`
  margin: ${convertToVwMob(8)} 0 0;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const TabsBox = styled.div`
  display: flex;
  border-radius: ${convertToVwMob(10)} ${convertToVwMob(10)} 0 0;
  border: 1px solid #eeeeee;
  background: #ffffff;
  overflow: hidden;
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${convertToVwMob(12)} ${convertToVwMob(20)};
  display: inline-flex;
  gap: ${convertToVwMob(10)};
  align-items: center;
  justify-content: flex-start;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${convertToVwMob(3)};
    border-radius: 0;
    background: ${({ $active }) => ($active ? '#2BC2E1' : 'transparent')};
  }
`;

const TabIcon = styled.img`
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  object-fit: contain;
`;

const PanelCard = styled.div`
  border-radius: ${convertToVwMob(10)};
  border: 1px solid #eeeeee;
  background: #ffffff;
  padding: ${convertToVwMob(18)};
`;

const FieldTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const CurrencyRow = styled.div`
  margin-top: ${convertToVwMob(10)};
  display: flex;
  gap: ${convertToVwMob(18)};
  align-items: center;
`;

const CurrencyBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const RadioDot = styled.span<{ $active: boolean }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  border-radius: 999px;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const InputBox = styled.div<{ $error?: boolean }>`
  border-radius: ${convertToVwMob(4)};
  border: ${({ $error }) => ($error ? '1.6px solid #ff0000' : '1px solid #EEEEEE')};
  background: #ffffff;
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
  }

  &:focus-within {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
    box-shadow: ${({ $error }) =>
      $error ? '0 0 0 2px rgba(255, 0, 0, 0.25)' : '0 0 0 2px rgba(43, 194, 225, 0.2)'};
  }
`;

const TitleInputBox = styled.div<{ $error?: boolean }>`
  margin-top: ${convertToVwMob(8)};
  width: 100%;
  max-width: 353px;
  border-radius: ${convertToVwMob(4)};
  border: ${({ $error }) => ($error ? '1.6px solid #ff0000' : '1px solid #eeeeee')};
  background: #ffffff;
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
  }

  &:focus-within {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
    box-shadow: ${({ $error }) =>
      $error ? '0 0 0 2px rgba(255, 0, 0, 0.25)' : '0 0 0 2px rgba(43, 194, 225, 0.2)'};
  }
`;

const InputLabel = styled.div`
  color: #676767;
 ${({ theme }) => theme.typography.contents.medium20}
`;

const InputField = styled.input<{ $error?: boolean }>`
  width: 100%;
  border: none;
  outline: none;

  color: ${({ $error }) => ($error ? '#ff0000' : '#17171b')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  background: transparent;
`;

const GoalPreview = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
`;

const InputTextArea = styled.textarea<{ $error?: boolean }>`
  margin-top: ${convertToVwMob(6)};
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  color: ${({ $error }) => ($error ? '#ff0000' : '#17171b')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  background: transparent;
`;

const Divider = styled.div<{ $fullWidth?: boolean }>`
  margin: ${convertToVwMob(18)} 0;
  height: 1px;
  background: #EEEEEE;
  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    margin-left: -${convertToVwMob(18)};
    margin-right: -${convertToVwMob(18)};
    width: calc(100% + ${convertToVwMob(36)});
  `}
`;

const HelperText = styled.div<{ $error?: boolean }>`
  margin-top: ${convertToVwMob(8)};
  color: ${({ $error }) => ($error ? '#ff0000' : '#357b89')};
  font-size: ${convertToVwMob(16)};
  ${theme.typography.contents.regular16}
`;

const ButtonRow = styled.div`
  margin-top: ${convertToVwMob(18)};
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(16)};
`;

const ActionButton = styled.button<{ $variant: 'cancel' | 'save'; $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(4)};
  cursor: pointer;
  white-space: nowrap;
${({ theme }) => theme.typography.button.regular20}
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;

  ${({ $variant }) =>
    $variant === 'cancel'
      ? `
    color: #676767;
    border: 1px solid #DFDFDF;
    background: #FFF;
  `
      : `
    color: #FFF;
    border: none;
    background: #026478;
  `}

  ${({ $variant, $loading }) =>
    $variant === 'save' && $loading
      ? `
    border-radius: ${convertToVwMob(10)};
    background: #EBFCFF;
    color: #2BC2E1;
  `
      : ''}

  opacity: ${({ disabled, $loading }) => (disabled ? ($loading ? 1 : 0.5) : 1)};
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${convertToVwMob(12)} 0;
`;

const InfoLabel = styled.div`
  flex-shrink: 0;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const InfoValue = styled.div`
  flex: 1;
  min-width: 0;
  text-align: right;
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${convertToVwMob(12)};
  border-bottom: 1px solid #eeeeee;
  margin-left: calc(-1 * ${convertToVwMob(18)});
  margin-right: calc(-1 * ${convertToVwMob(18)});
  padding-left: ${convertToVwMob(18)};
  padding-right: ${convertToVwMob(18)};
`;

const CardTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const EditButton = styled.button`
  border-radius: ${convertToVwMob(100)};
  border: 1px solid #82e8ff;
  background: #ffffff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  padding: ${convertToVwMob(8)} ${convertToVwMob(20)};
  cursor: pointer;
`;

const MediaBox = styled.div<{ $hasImage?: boolean }>`
  margin-top: ${convertToVwMob(12)};
  border-radius: ${convertToVwMob(10)};
  border: 2px solid #2bc2e1;
  background: #f0f1f2;
  height: ${({ $hasImage }) => ($hasImage ? 'auto' : convertToVwMob(220))};
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'flex')};
  align-items: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  justify-content: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  position: relative;
  overflow: hidden;
`;

const MediaImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const Badge = styled.div`
  position: absolute;
  top: ${convertToVwMob(12)};
  left: ${convertToVwMob(12)};
  background: #2bc2e1;
  color: #ffffff;
  border-radius: ${convertToVwMob(6)};
  padding: ${convertToVwMob(6)} ${convertToVwMob(10)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;
`;

const MediaIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${convertToVwMob(20)};
`;

const ModalContent = styled.div`
  width: 100%;
  background: #fff;
  border-radius: ${convertToVwMob(20)};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(14)};
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: ${convertToVwMob(20)};
  color: #bcbcbc;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: ${convertToVwMob(20)};
  flex: 1;
  overflow-y: auto;
  max-height: 70vh;
`;

const StoryEditOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: none;
`;

const StoryEditShell = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const StoryEditHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9001;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${convertToVwMob(STORY_EDIT_HEADER_HEIGHT)};
  padding: 0 ${convertToVwMob(20)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const StoryEditHeaderBtn = styled.button<{ $primary?: boolean }>`
  background: none;
  border: none;
  ${({ theme }) => theme.typography.contents.medium20}
  font-size: ${convertToVwMob(20)};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90])};
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${convertToVwMob(30)};
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const StoryEditScroll = styled.div<{ $topPad?: number; $bottomPad?: number }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: ${({ $topPad }) => ($topPad ? convertToVwMob($topPad) : 0)};
  padding-bottom: ${({ $bottomPad }) => ($bottomPad ? convertToVwMob($bottomPad) : 0)};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StoryEditBody = styled.div`
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(20)};
`;

const StoryEditTapArea = styled.div<{ $focused?: boolean }>`
  width: 100%;
  border-radius: ${({ $focused }) => ($focused ? 0 : convertToVwMob(4))};
  border: ${({ $focused, theme }) => ($focused ? 'none' : `1px solid ${theme.colors.grayScale[20]}`)};
  background: ${({ $focused, theme }) => ($focused ? 'transparent' : theme.colors.grayScale[0])};
  overflow: ${({ $focused }) => ($focused ? 'visible' : 'hidden')};

  > div {
    margin-top: 0;
  }

  > div > div {
    margin-top: 0;
    border-radius: 0;
  }
`;

const StoryKeyboardFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 9002;
  display: flex;
  flex-direction: column;
  padding: ${convertToVwMob(8)};
  padding-bottom: calc(${convertToVwMob(8)} + env(safe-area-inset-bottom));
  background: #fdfdfd;
  border-top: 1px solid #bcbcbc;
  flex-shrink: 0;
`;

const StoryReadonlyBox = styled.div`
  margin-top: ${convertToVwMob(12)};
`;

const StoryReadonlyHtml = styled(SafeHtmlViewer)`
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  figure[data-embed='story-image'],
  figure[data-embed='pinned-image'] {
    margin: ${convertToVwMob(12)} 0;
    border-radius: ${convertToVwMob(10)};
    overflow: hidden;
    border: 2px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='story-image'] > img[data-embed='image'],
  figure[data-embed='pinned-image'] img[data-embed='image'] {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }

  button[data-embed='delete-btn'] {
    display: none;
  }
`;

const StoryRawHtml = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const StoryRawHtmlEditor = styled.textarea`
  width: 100%;
  min-height: ${convertToVwMob(480)};
  border: none;
  outline: none;
  resize: vertical;
  background: transparent;
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const StoryAddPhotoButton = styled.button`
  display: flex;
  width: 100%;
  height: ${convertToVwMob(46)};
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: #ffffff;
  box-sizing: border-box;
  cursor: pointer;
  color: #676767;
  padding: 0;
  flex-shrink: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const StoryAddPhotoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 130%;
  color: #676767;
`;

const StoryAddPhotoIcon = styled.img`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  margin-right: ${convertToVwMob(8)};
`;

const SectionDesc = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  margin-bottom: ${convertToVwMob(20)};
  line-height: 1.4;
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(24)};
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(6)};
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
`;

const RadioInput = styled.input`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  border-radius: 50%;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease;
  }

  &:checked::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const FileRow = styled.div`
  display: flex;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(16)};
`;

const FileNameBox = styled.div`
  flex: 1;
  height: ${convertToVwMob(44)};
  background: #f8f8f8;
  border-radius: ${convertToVwMob(4)};
  padding: 0 ${convertToVwMob(10)};
  display: flex;
  align-items: center;
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachBtn = styled.button`
  width: ${convertToVwMob(80)};
  height: ${convertToVwMob(44)};
  background: #fff;
  border: 1px solid #bcbcbc;
  border-radius: ${convertToVwMob(4)};
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  cursor: pointer;

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;

/** 새 프로젝트 생성(NewProject mob) MediaPreview와 동일 */
const PreviewArea = styled.div<{ $hasMedia?: boolean }>`
  margin-top: ${convertToVwMob(12)};
  height: ${convertToVwMob(220)};
  border-radius: ${convertToVwMob(10)};
  border: ${({ $hasMedia }) => ($hasMedia ? '3px solid #2BC2E1' : '1px solid #e5e5e5')};
  background: #f0f1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const PreviewEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(10)};
  text-align: center;
  padding: ${convertToVwMob(8)};
`;

const PreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
  min-height: ${convertToVwMob(42)};
`;

const PreviewIcon = styled.img`
  width: ${convertToVwMob(48)};
  height: ${convertToVwMob(48)};
  object-fit: contain;
`;

const PreviewIconSlot = styled.div`
  height: ${convertToVwMob(70)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewVideoIcon = styled.svg`
  width: ${convertToVwMob(70)};
  height: ${convertToVwMob(70)};
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalFooter = styled.div`
  padding: ${convertToVwMob(16)} ${convertToVwMob(31)} ${convertToVwMob(16)};
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(16)};
`;

const ModalActionBtn = styled.button<{ $variant: 'cancel' | 'save'; $loading?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(4)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  white-space: nowrap;

  ${({ $variant }) =>
    $variant === 'cancel'
      ? `
    background: #fff;
    border: 1px solid #DFDFDF;
    color: #676767;
  `
      : `
    background: #026478;
    border: none;
    color: #fff;
  `}

  ${({ $variant, $loading }) =>
    $variant === 'save' && $loading
      ? `
    border-radius: 5px;
    background: #EBFCFF;
    color: #2BC2E1;
  `
      : ''}

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
`;

const VideoUrlRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(16)};
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: ${convertToVwMob(6)};
  border: ${convertToVwMob(2)} solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
  min-height: ${convertToVwMob(40)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-bottom: ${convertToVwMob(6)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: ${convertToVwMob(56)};
  width: ${convertToVwMob(96)};
  border-radius: ${convertToVwMob(12)};
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;


const EmptyText = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  white-space: pre-line;
`;

const BottomInset = styled.div`
  height: ${convertToVwMob(90)};
`;
